/**
 * Redirect Handler Utility
 * Handles post-verification redirects back to merchant sites
 */

import { logger } from './logger';

const redirectLogger = logger.clone({ labels: { component: 'RedirectHandler' }});

export interface RedirectParams {
  sessionId: string;
  status: 'verified' | 'failed' | 'cancelled';
  returnUrl?: string;
  /** Optional cancel redirect for new-tab abandonment */
  cancelUrl?: string;
  externalUserId?: string;
}

export function buildMerchantCallbackUrl(params: RedirectParams): string {
  if (!params.returnUrl) {
    throw new Error('No return URL provided');
  }

  const redirectUrl = new URL(decodeURIComponent(params.returnUrl));
  redirectUrl.searchParams.set('sessionId', params.sessionId);
  redirectUrl.searchParams.set('status', params.status);
  redirectUrl.searchParams.set('timestamp', Date.now().toString());

  if (params.externalUserId) {
    redirectUrl.searchParams.set('externalUserId', params.externalUserId);
  }

  return redirectUrl.toString();
}

/**
 * Redirects user back to merchant with verification result
 */
export function handleVerificationComplete(params: RedirectParams): void {
  const { sessionId, status, returnUrl, cancelUrl } = params;

  redirectLogger.info('Handling verification completion:', {
    labels: {
      sessionId: sessionId.substring(0, 8) + '...',
      status,
      hasReturnUrl: `${!!returnUrl}`,
      hasCancelUrl: `${!!cancelUrl}`,
    }
  });

  // Completion redirects always use returnUrl
  const targetUrl = returnUrl;

  if (!targetUrl) {
    redirectLogger.error('No redirect URL available for status:', { labels: { status }});
    // Show error message instead of redirecting
    showRedirectError('No return URL provided');
    return;
  }

  try {
    redirectLogger.info('Attempting to build redirect URL:', {
      labels: {
        targetUrl: targetUrl,
        targetUrlType: typeof targetUrl,
        targetUrlLength: `${targetUrl?.length}`,
      }
    });

    const redirectUrl = new URL(buildMerchantCallbackUrl(params));

    redirectLogger.info('Redirecting to:', {
      labels: {
        domain: redirectUrl.hostname,
        status,
        sessionId: sessionId.substring(0, 8) + '...',
      }
    });

    // Perform redirect
    window.location.href = redirectUrl.toString();
  } catch (error) {
    redirectLogger.error('Failed to build redirect URL:', { error: error instanceof Error ? error : new Error(`${error}`) });
    showRedirectError('Invalid return URL format');
  }
}

/**
 * Validates redirect URLs to prevent open redirect attacks
 */
export function validateRedirectUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);

    // Only allow HTTPS in production (allow HTTP for localhost)
    const isLocalhost =
      parsedUrl.hostname === 'localhost' ||
      parsedUrl.hostname === '127.0.0.1' ||
      parsedUrl.hostname.endsWith('.localhost');

    if (!isLocalhost && parsedUrl.protocol !== 'https:') {
      redirectLogger.warn(
        'Rejected non-HTTPS redirect URL:',
        { labels: { hostname: parsedUrl.hostname }}
      );
      return false;
    }

    // Additional security checks can be added here
    // - Domain allowlist
    // - Path restrictions
    // - Parameter validation

    return true;
  } catch {
    redirectLogger.warn('Invalid redirect URL format:', { labels: { url }});
    return false;
  }
}

/**
 * Shows error message when redirect fails
 */
function showRedirectError(message: string): void {
  // For now, just log the error
  // In a real implementation, this would show a user-friendly error page
  redirectLogger.error('Redirect error:', { labels: { message }});

  // You could dispatch an action here to show an error component
  // or redirect to a generic error page
  alert(`Verification complete, but unable to redirect: ${message}`);
}
